
import $ from 'jquery';

import {cleanUrl, replaceGerUmlaut} from './../general/Util.js';
import FilterRedirectionBuilder from './FilterRedirection.js';

const specialBrandSlugs = [
    'dior',
    'chanel'
];

const fetchFilterJSON = async() => {

    if ( window.filterJSON ) {
        return window.filterJSON;
    }

    const url = new URL( document.location.href );
    url.searchParams.append( 'filterJSON', 1 );

    const options =  {
        method: 'GET',
        credentials: 'include',
        mode: 'no-cors',
    };

    return await fetch( url.toString(), options ).then( res => res.json() );
};

/**
 * Split the given url into separate URLParts.
 *
 * @param  {string} currentUrl URL to split, defaults to current URL.
 * @return {Object}            Simple Object with the URL parts.
 */
const getCurrentUrlParts = ( url = null ) => {

    const currentUrl = url || window.location.pathname;

    const urlParts = {};
    currentUrl.split( '/' ).filter( i => i ).forEach( ( part ) => {

        let split;
        if ( part.startsWith( 'filter-' ) ) {
            const tmp = part.split( '-' );
            split = [ tmp.slice( 0, 2 ).join('-'), ...tmp.slice( 2 ) ];
        } else {
            split = part.split( '-' );
        }

        if ( 'markenshop' === split[ split.length - 1 ] ) {
            const brandshopNames = [split.slice( 0, -1 ).join('-')];
            const brandshopIdent = split.slice(-1);

            urlParts['markenshop'] = [ ...brandshopNames, ...brandshopIdent ];
        } else if ( specialBrandSlugs.includes( split[0] ) ) {
            urlParts['shop'] = split;
        } else {
            urlParts[ split[0] ] = split.slice(1);
        }
    });

    return urlParts;
};

/**
 * Convert given serialized formdata object into key/array pairs.
 * Also handles special values, that need conversion into different formats.
 *
 * @param  {Object} newData New Data to convert.
 * @return {Object}         Data split into key/array pairs.
 */
const getFilteredUrlParts = function( newData = {} ) {

    const urlParts = {};
    Object.keys( newData ).forEach( key => {

        let newValue = newData[ key ];

        switch ( key ) {

            // Sale/New are boolean fields of 0/1.
            case 'sale':
            case 'new':
                newValue = parseInt( newData[ key ] );
            break;

            // Parse Price to correct formatting.
            case 'price':
                const price = newData[ key ];
                const minPrice = Math.max( Math.min( price.from, price.to ), price.min );
                const maxPrice = Math.min( Math.max( price.from, price.to ), price.max );

                if ( minPrice === Math.min( price.min ) && maxPrice === Math.max( price.max ) ) {
                    newValue = [];
                } else {
                    newValue = [ minPrice, maxPrice ];
                }
            break;

            // Split sort_select value to sortfield and sortorder keys.
            case 'sort_select':
                const [sortfield,sortorder] = newData[ key ].split( '|' );
                urlParts['sortfield'] = [sortfield];
                urlParts['sortorder'] = [sortorder];

            // Continue here with next loop, do not add the sort_select key, since we break it up into the above two.
            return true;

            case 'shop':
                if ( 'string' === typeof newValue ) {
                    newValue = newValue.split('-');

                    // Remove first parameter, if it is the same.
                    if ( newValue[0] === key ) {
                        newValue = newValue.slice( 1 );
                    }
                }
            break;
        }

        // Filter out any empty values.
        urlParts[ key ] = [].concat( newValue ).filter( testValue => testValue );
    });

    return urlParts;
};



const prepareFilterUrl = function( newData = {}, currentUrl = null ) {

    const parts = Object.assign(
        getCurrentUrlParts(currentUrl),
        getFilteredUrlParts(newData)
    );

    if ( parts.markenshop && parts.brand ) {
        parts.markenshop = [ ...parts.brand, parts.markenshop[parts.markenshop.length -1] ];
    }

    // Fix Parts.
    const Redirector = new FilterRedirectionBuilder( parts );

    const urlParts = Redirector
        .redirectFromMarkenShopToShop()
        .redirectFromShopToMarkenshop()
        .checkForSpecialBrands()
        .getUrlParts();

    // This map allows a concrete sortorder for all keys.
    // Keys not in the map will be added via the Object.keys part in no particular order.
    const keyMap = [

        // Important keys.
        'shop',
        'markenshop',

        // Other custom keys.
        'brand',
        'theme',
        'badge',
        'pseries',
        'size',
        'color',
        'saison',
        'price',
        'sale',
        'new',
        'salesrange',

        // So any additional custom keys are placed correctly.
        ...Object.keys( urlParts ),

        // Sortkeys at the end.
        'sortfield',
        'sortorder',
    ];

    // Remove current page from filter.
    const orderedKeys = keyMap.filter( key => key !== 'p' )

    // Remove other duplicated values. ( array_unique )
    .filter( (value, index) => keyMap.indexOf( value ) === index )

    // Sort the keys, in case they somehow got mixed up.
    .sort( ( a, b ) => {

        const indexA = keyMap.indexOf( a );
        const indexB = keyMap.indexOf( b );

        return indexA - indexB;
    });

    // Concat values again to make clean arrays.
    const urlDataArray = orderedKeys.map( key => {

        if ( 'undefined' === typeof urlParts[ key ] ) {
            return false;
        }

        const values = [].concat( urlParts[ key ] ).map( part => {

            // Only replace when we handle strings.
            if ( 'string' !== typeof part ) {
                return part;
            }

            if ( 'markenshop' === key ) {
                return replaceGerUmlaut( decodeURIComponent( part ) ).replace( /[\#\/]/g, ( match ) => encodeURIComponent( match ) ).toLowerCase();
            }

            if ( 'size' === key ) {
                return replaceGerUmlaut( decodeURIComponent( part.replace( /-/g, '~' ).replace( /\//g, '^' ) ) ).replace( /[\#\/]/g, ( match ) => encodeURIComponent( match ) ).toLowerCase().replace( ' ', '%20' );
            }

            // Encode URI Components correctly, and replace special characters.
            return replaceGerUmlaut( decodeURIComponent( part.replace( /-/g, '~' ).replace( /\//g, '^' ) ) ).replace( /[\#\/]/g, ( match ) => encodeURIComponent( match ) ).toLowerCase();
        }).filter( testValue => testValue );

        if ( 'markenshop' === key ) {
            key = '';
        } else if ( 'shop' === key ) {
            if ( specialBrandSlugs.includes( values[0] ) ) {
                key = '';
            }

        } else if ( values.length === 0 ) {
            // Delete this filter.
            return false;
        }

        // Filter brands alphabetically.
        if ( 'brand' === key ) {
            values.sort();
        }


        if ( 'price' === key ) {
            // Append min and max price - add zeroes for all missing values, and sort by value size.
            const priceValues = [].concat( [ ...values ], [ 0, 0 ] ).map( pVal => parseFloat( pVal ) ).slice( 0, 2 ).sort( ( a, b ) => a - b );

            // If both values are zero we return false - this should be caught further up, but this check is here for sanity reasons.
            if ( priceValues.filter( testValue => testValue ).length === 0 ) {
                // Delete this filter.
                return false;
            }

            // Build special structure for pricings.
            return [ key, priceValues[0], '', priceValues[1] ];
        }

        // Return values so we can build {key}-{value} type URLs.
        return [ key, ...values ].filter( i => i );
    })

    // Filter invalid values.
    .filter( i => i )

    // Then join them via dash.
    .map( parts => parts.join( '-' ) );

    // and finally join them via '/'.
    return cleanUrl( [ '', ...urlDataArray, '' ].join( '/' ).replace( /\/+/, '/' ).replace( 'sortfield-bestseller/sortorder-asc/', '' ) );
};


$( document ).ready( async function() {

    if ( ! window.hasFilterJSON ) {
        return;
    }

    const filterJSON = await fetchFilterJSON( document.location.href );

    // Expose filterJSON.
    window.filterJSON = filterJSON;
    $( window ).trigger( 'filterjson_loaded.acl', filterJSON );
});

window.prepareFilterUrl = prepareFilterUrl;

export {
    specialBrandSlugs,
    getCurrentUrlParts,
    getFilteredUrlParts,
    fetchFilterJSON,
    prepareFilterUrl
}
